import { ref } from 'vue';
import {
  hasBaggage,
  isRefundable,
  isDirectFlight,
  byAirline,
  byPrice,
  byFlightAirport,
  byFlightArrDepTime,
  byFlightDuration,
  byTransferDuration,
  getDurationRange,
  getOffersPriceRange,
  getTransfersDurationRange,
  setAllAirlines,
  type FilterState,
  type FilterFunction,
} from '@aviata/offers-filters-fns';
import { useFilters } from '@aviata/offers-filters-composable';
import type { Airflow, Dict } from '@aviata/platform-types';
import { flightConnectionState } from 'shared';

import { DEFAULT_FILTER_LIMITS, TIME_INTERVALS } from './config';
import type { FilterLimits } from './types';

type InitPayload = {
  offers: Airflow.Offer.Entity[];
  flightsNumber: number;
};

export function byFlightTransfersNumber(
  offer: Airflow.Offer.Entity,
  filterParams: FilterState,
) {
  return offer.flights.reduce((filter, flight, i) => {
    const { transfers } = filterParams.flights[i];

    if (transfers.length === 0) {
      return true;
    }

    return Math.max(...transfers) >= flight.segments.length - 1 && filter;
  }, true);
}

const { isNightTimeTransfer } = flightConnectionState.lib;

function byNightTransfer(
  offer: Airflow.Offer.Entity,
  filterParams: FilterState & { isWithoutNightTransfer: boolean },
) {
  if (!filterParams.isWithoutNightTransfer) {
    return true;
  }

  return offer.flights.every((flight: Airflow.Offer.Flight) => {
    if (flight.segments.length <= 1) {
      return true;
    }

    for (let i = 1; flight.segments.length > i; i++) {
      const arrivedAt = flight.segments[i - 1].arr.at;
      const departureAt = flight.segments[i].dep.at;

      if (isNightTimeTransfer(arrivedAt, departureAt)) {
        return false;
      }
    }

    return true;
  });
}

const hasNightTimeTransfer = (flights: Airflow.Offer.Flight[]) =>
  flights.some(flight =>
    isNightTimeTransfer(
      flight.segments[flight.segments.length - 1].arr.at,
      flight.segments[0].dep.at,
    ),
  );

const flightHasRecheck = (flight: Airflow.Offer.Flight) =>
  flight.segments.some(segment => segment.baggage?.recheck_required === true);

const hasRecheckTransfer = (flights: Airflow.Offer.Flight[]) =>
  flights.some(flightHasRecheck);

function byRecheckTransfer(
  offer: Airflow.Offer.Entity,
  filterParams: FilterState & { isWithoutRecheck: boolean },
) {
  if (!filterParams.isWithoutRecheck) {
    return true;
  }

  return !offer.flights.some(flightHasRecheck);
}

const filters = [
  hasBaggage,
  isRefundable,
  isDirectFlight,
  byAirline,
  byPrice,
  byFlightAirport,
  byFlightArrDepTime,
  byFlightDuration,
  byFlightTransfersNumber,
  byNightTransfer,
  byRecheckTransfer,
  byTransferDuration,
] as FilterFunction[];

export function useOffersFilters(airlines: Dict.Entity['airlines']) {
  const filtersState = useFilters({ airlines, filters });
  const limits = ref<FilterLimits>(DEFAULT_FILTER_LIMITS);

  function init({ offers, flightsNumber }: InitPayload) {
    const flights = offers.flatMap(offer => offer.flights);
    // @ts-expect-error types overlap
    const flightDurationRange = getDurationRange(flights);
    // @ts-expect-error types overlap
    const transferDurationRange = getTransfersDurationRange(flights);
    const priceRange = getOffersPriceRange(offers);
    const initialState = {
      prices: priceRange,
      flights: [...Array(flightsNumber)].map(() => ({
        dep: { time: TIME_INTERVALS, airports: [] },
        arr: { time: TIME_INTERVALS, airports: [] },
        transfers: [],
        transfersDuration: transferDurationRange,
        duration: flightDurationRange,
      })),
      isWithoutNightTransfer: hasNightTimeTransfer(flights) ? false : null,
      isWithoutRecheck: hasRecheckTransfer(flights) ? false : null,
      isImplicitAllAirlines: false,
      airlines: setAllAirlines(filtersState.state.value.airlines, false),
    };

    filtersState.init({ initialState, stateProps: { flightsNumber } });
    limits.value = {
      prices: priceRange,
      transferDuration: transferDurationRange,
      flightDurationRange,
    };
  }

  return {
    limits,
    state: filtersState.state,
    init,
    filterOffers: filtersState.filterOffers,
    onFilterUpdate: filtersState.onCommonFilterUpdate,
    onFlightFilterUpdate: filtersState.onFlightFilterUpdate,
  };
}
